import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useAdminAuth } from '@/shared/hooks/useAdminAuth';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsInTrial } from '@/features/billing/redux/trial-selectors';

export const STORAGE_KEY = 'trial-activated-modal-shown';

/**
 * GH#1829 — ¿hay un input activo? El modal nunca debe robarle el foco a un cliente
 * que está escribiendo (se medía el teléfono guardado como "+1 809 5").
 */
function hayInputActivo(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * GH#1829 — el modal solo se auto-muestra en el tablero, nunca sobre un formulario
 * a medio llenar de otra pantalla.
 */
function enTablero(pathname: string): boolean {
  return pathname.endsWith('/dashboard');
}

export interface TrialModalTrigger {
  trigger?: 'onboarding_completed' | 'registration_completed' | 'manual' | string;
  delay?: number;
  force?: boolean; // Si true, ignora localStorage y muestra de nuevo
}

export interface UseTrialModalReturn {
  isOpen: boolean;
  show: (options?: TrialModalTrigger) => void;
  hide: () => void;
  hasBeenShown: () => boolean;
  reset: () => void; // Para testing/desarrollo
}

/**
 * Hook para manejar el modal de trial activado
 * 
 * Características:
 * - Se muestra una sola vez (controlado por localStorage)
 * - Extensible para múltiples triggers
 * - Puede dispararse desde cualquier componente
 * - Mantiene compatibilidad con el comportamiento actual
 * 
 * @param user - Usuario opcional. Si no se proporciona, el hook intentará obtenerlo automáticamente
 */
export const useTrialModal = (user?: any): UseTrialModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const authUser = useAuth();
  const adminUser = useAdminAuth();
  
  // Seleccionar usuario: usar el proporcionado o detectar automáticamente
  const systemRoles = ['SUPER_ADMIN', 'AGENCY_ADMIN', 'PROJECT_DEVELOPER_ADMIN'];
  const isAuthUserSystemRole = authUser.user && systemRoles.includes(authUser.user.role);
  const shouldUseFallback = isAuthUserSystemRole && authUser.isAuthenticated && !adminUser.user;
  const currentUser = user || (isAdminRoute
    ? adminUser.user || (shouldUseFallback ? authUser.user : null)
    : authUser.user);
  // pmo #1655 (GH#1565): el aviso de prueba solo tiene sentido si HAY prueba.
  // Un SUPER_ADMIN, un plan pago o una prueba vencida no la tienen.
  const isInTrial = useSelector(selectIsInTrial);

  /**
   * Verifica si el modal ya fue mostrado
   */
  const hasBeenShown = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }, []);

  /**
   * Muestra el modal con opciones configurables
   */
  const show = useCallback((options?: TrialModalTrigger) => {
    const { delay = 1500, force = false } = options || {};
    
    // Si no es forzado y ya se mostró, no hacer nada
    if (!force && hasBeenShown()) {
      return;
    }

    // Limpiar timer anterior si existe
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Delay opcional antes de mostrar
    timerRef.current = setTimeout(() => {
      setIsOpen(true);
      timerRef.current = null;
    }, delay);
  }, [hasBeenShown]);

  /**
   * Oculta el modal y guarda en localStorage
   */
  const hide = useCallback(() => {
    // Limpiar timer si existe
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }, []);

  /**
   * Resetea el estado (útil para testing)
   */
  const reset = useCallback(() => {
    // Limpiar timer si existe
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  /**
   * Auto-trigger cuando se completa el onboarding.
   *
   * GH#1829 — antes esto disparaba un timer 1500ms después en CUALQUIER pantalla:
   * el modal saltaba encima de un formulario a medio llenar (ej. /agencia/agentes/
   * nuevo), robaba el foco y se comía lo que el cliente escribía (teléfono guardado
   * como "+1 809 5"). Ahora solo se auto-muestra en el tablero y, al disparar,
   * nunca roba el foco de un input activo. Tras el onboarding el usuario cae en
   * /agencia/dashboard, así que el modal sigue apareciendo donde corresponde.
   */
  useEffect(() => {
    if (!currentUser || !currentUser.onboardingCompleted) return;
    if (!isInTrial) return;
    if (hasBeenShown()) return;
    if (!enTablero(location.pathname)) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Chequeo en el momento de disparar: si el cliente está escribiendo, no abrir.
      if (hayInputActivo()) return;
      setIsOpen(true);
      // pmo #1654 (GH#1683): se marca como mostrado AL ABRIRSE, no al cerrarse.
      // Si el cliente se iba a otra pantalla sin tocarlo, volvía a saltar en cada
      // visita al tablero. Como en GHL, el aviso de prueba se ve una sola vez.
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch {
        /* sin storage: a lo sumo se vuelve a ver una vez */
      }
    }, 1500);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentUser, isInTrial, hasBeenShown, location.pathname]);

  return {
    isOpen,
    show,
    hide,
    hasBeenShown,
    reset,
  };
};

